import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCart } from './CartContext';
import { useCurrency } from './CurrencyContext';
import { fetchCustomerRentals, cancelRental } from './rentalApi';
import {
  Box, Typography, Container, Paper, Stack, Chip, Button, Card,
  CardMedia, Tabs, Tab, Dialog, DialogTitle, DialogContent, DialogActions,
  Alert, Snackbar, CircularProgress, Divider, Avatar, IconButton, Tooltip,
  alpha, useTheme, useMediaQuery, Skeleton, Badge, LinearProgress, Stepper, Step, StepLabel
} from '@mui/material';
import { keyframes } from '@mui/material/styles';
import HomeRepairServiceIcon from '@mui/icons-material/HomeRepairService';
import ScheduleIcon from '@mui/icons-material/Schedule';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import WarningIcon from '@mui/icons-material/Warning';
import AssignmentReturnIcon from '@mui/icons-material/AssignmentReturn';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import PaletteIcon from '@mui/icons-material/Palette';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import TimerIcon from '@mui/icons-material/Timer';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import StorefrontIcon from '@mui/icons-material/Storefront';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PersonIcon from '@mui/icons-material/Person';
import PhoneIcon from '@mui/icons-material/Phone';
import DirectionsBikeIcon from '@mui/icons-material/DirectionsBike';

const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const STATUS_CONFIG = {
  Pending: { color: '#FF9800', icon: <ScheduleIcon />, labelAr: 'قيد الانتظار', labelEn: 'Pending' },
  Confirmed: { color: '#2196F3', icon: <CheckCircleIcon />, labelAr: 'مؤكد', labelEn: 'Confirmed' },
  Active: { color: '#4CAF50', icon: <HomeRepairServiceIcon />, labelAr: 'نشط', labelEn: 'Active' },
  Returned: { color: '#9E9E9E', icon: <AssignmentReturnIcon />, labelAr: 'تم الإرجاع', labelEn: 'Returned' },
  Overdue: { color: '#F44336', icon: <WarningIcon />, labelAr: 'متأخر', labelEn: 'Overdue' },
  Cancelled: { color: '#757575', icon: <CancelIcon />, labelAr: 'ملغي', labelEn: 'Cancelled' },
};

const PRICE_TYPE_LABELS = {
  hourly: { ar: 'ساعات', en: 'Hours', arSingle: 'ساعة', enSingle: 'Hour' },
  daily: { ar: 'أيام', en: 'Days', arSingle: 'يوم', enSingle: 'Day' },
  weekly: { ar: 'أسابيع', en: 'Weeks', arSingle: 'أسبوع', enSingle: 'Week' },
  monthly: { ar: 'أشهر', en: 'Months', arSingle: 'شهر', enSingle: 'Month' },
};

const DELIVERY_STATUS_CONFIG = {
  Assigned: { labelAr: 'تم التعيين', labelEn: 'Assigned', color: '#FF9800', icon: '📋' },
  PickedUp: { labelAr: 'تم الاستلام', labelEn: 'Picked Up', color: '#2196F3', icon: '📦' },
  OnTheWay: { labelAr: 'في الطريق', labelEn: 'On The Way', color: '#9C27B0', icon: '🚚' },
  Delivered: { labelAr: 'تم التوصيل', labelEn: 'Delivered', color: '#4CAF50', icon: '✅' },
  ReturnPickup: { labelAr: 'استلام المرتجع', labelEn: 'Return Pickup', color: '#FF5722', icon: '🔄' },
  ReturnDelivered: { labelAr: 'تم إرجاع المنتج', labelEn: 'Return Delivered', color: '#607D8B', icon: '✅' },
};

const DELIVERY_STAGES = ['Assigned', 'PickedUp', 'OnTheWay', 'Delivered'];

const getImageUrl = (img) => {
  if (!img) return null;
  if (typeof img === 'string') { try { const p = JSON.parse(img); return p.medium || p.thumbnail || img; } catch { return img; } }
  if (typeof img === 'object') return img.medium || img.thumbnail || img.large;
  return null;
};

// ═══════════════════════════════════════════
// RENTAL ORDER CARD
// ═══════════════════════════════════════════

const RentalOrderCard = ({ order, isAr, currencySymbol, onViewDetails, onCancel, index }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const status = STATUS_CONFIG[order.Status] || STATUS_CONFIG.Pending;
  const durationLabel = PRICE_TYPE_LABELS[order.RentalPriceType] || { ar: '', en: '' };
  const imageUrl = getImageUrl(order.ProductImage);
  const canCancel = order.Status === 'Pending' || order.Status === 'Confirmed';

  const getProgress = () => {
    if (order.Status !== 'Active' && order.Status !== 'Overdue') return null;
    const start = new Date(order.RentalStartDate).getTime();
    const end = new Date(order.RentalEndDate).getTime();
    const now = Date.now();
    const total = end - start;
    if (total <= 0) return 100;
    return Math.min(100, Math.max(0, ((now - start) / total) * 100));
  };
  const progress = getProgress();

  const getDaysRemaining = () => {
    if (order.Status !== 'Active') return null;
    const diff = new Date(order.RentalEndDate).getTime() - Date.now();
    if (diff <= 0) return 0;
    return Math.ceil(diff / (1000 * 60 * 60 * 24));
  };
  const daysRemaining = getDaysRemaining();

  const deliveryStatusCfg = order.DeliveryStatus ? DELIVERY_STATUS_CONFIG[order.DeliveryStatus] : null;

  return (
    <Card
      sx={{
        borderRadius: { xs: 3, sm: 4 },
        overflow: 'hidden',
        transition: 'all 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
        border: '1px solid',
        borderColor: alpha(status.color, 0.15),
        boxShadow: `0 4px 20px ${alpha('#000', 0.04)}`,
        animation: `${fadeInUp} 0.4s ease-out ${index * 0.08}s both`,
        '&:hover': {
          transform: isMobile ? 'none' : 'translateY(-4px)',
          boxShadow: `0 12px 40px ${alpha(status.color, 0.12)}`,
        },
        mb: 2,
      }}
    >
      <Box sx={{ height: 4, bgcolor: status.color }} />

      <Stack direction={{ xs: 'column', sm: 'row' }} sx={{ p: 0 }}>
        {/* Image */}
        <Box sx={{
          width: { xs: '100%', sm: 160, md: 200 }, flexShrink: 0,
          position: 'relative', minHeight: { xs: 180, sm: 'auto' },
        }}>
          <CardMedia component="img" image={imageUrl || '/placeholder.png'} alt={order.ProductName}
            sx={{ width: '100%', height: '100%', objectFit: 'cover', minHeight: { xs: 180, sm: 200 } }} />
          <Chip label={`#${order.Id}`} size="small"
            sx={{ position: 'absolute', top: 8, left: 8, bgcolor: alpha('#000', 0.6), color: '#fff', fontWeight: 800, fontSize: '0.7rem', backdropFilter: 'blur(8px)' }} />
          <Chip
            icon={React.cloneElement(status.icon, { sx: { fontSize: 14, color: '#fff !important' } })}
            label={isAr ? status.labelAr : status.labelEn}
            size="small"
            sx={{ position: 'absolute', top: 8, right: 8, bgcolor: alpha(status.color, 0.9), color: '#fff', fontWeight: 700, fontSize: '0.65rem', '& .MuiChip-icon': { color: '#fff' } }}
          />
          {/* Delivery method badge */}
          <Chip
            icon={order.IsDelivery ? <LocalShippingIcon sx={{ fontSize: 12, color: '#fff !important' }} /> : <StorefrontIcon sx={{ fontSize: 12, color: '#fff !important' }} />}
            label={order.IsDelivery ? (isAr ? 'توصيل' : 'Delivery') : (isAr ? 'استلام' : 'Pickup')}
            size="small"
            sx={{ position: 'absolute', bottom: 8, left: 8, bgcolor: alpha(order.IsDelivery ? '#0284C7' : '#059669', 0.85), color: '#fff', fontWeight: 600, fontSize: '0.6rem', '& .MuiChip-icon': { color: '#fff' } }}
          />
        </Box>

        {/* Content */}
        <Box sx={{ flex: 1, p: { xs: 2, sm: 2.5 }, display: 'flex', flexDirection: 'column' }}>
          <Typography variant="h6" fontWeight={800} sx={{ fontSize: { xs: '1rem', sm: '1.1rem' }, mb: 0.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {order.ProductName}
          </Typography>

          {order.SelectedColor && (
            <Chip icon={<PaletteIcon sx={{ fontSize: 12 }} />} label={order.SelectedColor} size="small" variant="outlined" sx={{ alignSelf: 'flex-start', mb: 1, fontSize: '0.65rem', height: 22 }} />
          )}

          {/* Dates */}
          <Stack direction="row" spacing={1} sx={{ mb: 1, flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
            <Stack direction="row" spacing={0.5} alignItems="center">
              <CalendarMonthIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                {new Date(order.RentalStartDate).toLocaleDateString(isAr ? 'ar-EG' : 'en-US', { month: 'short', day: 'numeric' })}
              </Typography>
            </Stack>
            <Typography variant="caption" color="text.secondary">→</Typography>
            <Stack direction="row" spacing={0.5} alignItems="center">
              <CalendarMonthIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                {new Date(order.RentalEndDate).toLocaleDateString(isAr ? 'ar-EG' : 'en-US', { month: 'short', day: 'numeric' })}
              </Typography>
            </Stack>
            <Chip label={`${order.RentalDuration} ${isAr ? durationLabel.ar : durationLabel.en}`} size="small" variant="outlined" sx={{ fontSize: '0.6rem', height: 20 }} />
          </Stack>

          {/* Delivery status chip */}
          {deliveryStatusCfg && order.IsDelivery && (
            <Chip
              label={`${deliveryStatusCfg.icon} ${isAr ? deliveryStatusCfg.labelAr : deliveryStatusCfg.labelEn}`}
              size="small"
              sx={{ alignSelf: 'flex-start', mb: 1, bgcolor: alpha(deliveryStatusCfg.color, 0.1), color: deliveryStatusCfg.color, fontWeight: 700, fontSize: '0.65rem', height: 24 }}
            />
          )}

          {/* Driver info */}
          {order.DriverName && order.IsDelivery && (
            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 1 }}>
              <DirectionsBikeIcon sx={{ fontSize: 14, color: 'info.main' }} />
              <Typography variant="caption" color="info.main" fontWeight={600} sx={{ fontSize: '0.7rem' }}>
                {order.DriverName}
              </Typography>
            </Stack>
          )}

          {/* Progress bar for active */}
          {progress != null && (
            <Box sx={{ mb: 1.5 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ fontSize: '0.65rem' }}>
                  {isAr ? 'المدة المنقضية' : 'Time Elapsed'}
                </Typography>
                {daysRemaining != null && daysRemaining > 0 && (
                  <Stack direction="row" spacing={0.3} alignItems="center">
                    <TimerIcon sx={{ fontSize: 12, color: daysRemaining <= 2 ? 'error.main' : 'text.secondary' }} />
                    <Typography variant="caption" fontWeight={700} sx={{ fontSize: '0.65rem', color: daysRemaining <= 2 ? 'error.main' : 'text.secondary' }}>
                      {daysRemaining} {isAr ? 'يوم متبقي' : 'days left'}
                    </Typography>
                  </Stack>
                )}
              </Stack>
              <LinearProgress variant="determinate" value={progress}
                sx={{ height: 6, borderRadius: 3, bgcolor: alpha(status.color, 0.1), '& .MuiLinearProgress-bar': { borderRadius: 3, bgcolor: progress >= 100 ? 'error.main' : status.color } }} />
            </Box>
          )}

          {/* Price & Actions */}
          <Box sx={{ mt: 'auto', display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem', textTransform: 'uppercase', fontWeight: 600 }}>
                {isAr ? 'الإجمالي' : 'Total'}
              </Typography>
              <Typography variant="h6" fontWeight={900} color="primary.main" sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' }, lineHeight: 1 }}>
                {(order.GrandTotal || order.RentalTotalPrice || 0).toLocaleString()} {currencySymbol}
              </Typography>
            </Box>
            <Stack direction="row" spacing={1}>
              {canCancel && (
                <Button variant="outlined" color="error" size="small" onClick={() => onCancel(order)}
                  startIcon={<CancelIcon sx={{ fontSize: 14 }} />}
                  sx={{ fontSize: '0.7rem', fontWeight: 700, borderRadius: 2.5, textTransform: 'none' }}>
                  {isAr ? 'إلغاء' : 'Cancel'}
                </Button>
              )}
              <Button variant="contained" size="small" onClick={() => onViewDetails(order)}
                sx={{ fontSize: '0.7rem', fontWeight: 700, borderRadius: 2.5, textTransform: 'none', px: 2 }}>
                {isAr ? 'التفاصيل' : 'Details'}
              </Button>
            </Stack>
          </Box>

          {order.IsOverdue && (
            <Alert severity="error" sx={{ mt: 1.5, borderRadius: 2, fontSize: '0.75rem', py: 0.5 }}>
              {isAr ? '⚠️ هذا الطلب متأخر عن موعد الإرجاع!' : '⚠️ This order is overdue!'}
            </Alert>
          )}
        </Box>
      </Stack>
    </Card>
  );
};

// ═══════════════════════════════════════════
// DETAIL ROW HELPER
// ═══════════════════════════════════════════

const DetailRow = ({ label, value, color, bold }) => (
  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ py: 0.4 }}>
    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.82rem' }}>{label}</Typography>
    <Typography variant="body2" fontWeight={bold ? 800 : 600} sx={{ fontSize: bold ? '0.95rem' : '0.82rem', textAlign: 'right', maxWidth: '58%', color: color || 'text.primary' }}>
      {value}
    </Typography>
  </Stack>
);

// ═══════════════════════════════════════════
// DELIVERY TRACKING STEPPER
// ═══════════════════════════════════════════

const DeliveryTracker = ({ order, isAr }) => {
  if (!order.IsDelivery || !order.DeliveryStatus) return null;

  const currentIdx = DELIVERY_STAGES.indexOf(order.DeliveryStatus);
  const isReturn = order.DeliveryStatus === 'ReturnPickup' || order.DeliveryStatus === 'ReturnDelivered';

  if (isReturn) {
    const returnStages = ['ReturnPickup', 'ReturnDelivered'];
    const returnIdx = returnStages.indexOf(order.DeliveryStatus);
    return (
      <Box sx={{ mt: 1 }}>
        <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ mb: 1, display: 'block', fontSize: '0.75rem' }}>
          {isAr ? '🔄 تتبع الإرجاع' : '🔄 Return Tracking'}
        </Typography>
        <Stepper activeStep={returnIdx} alternativeLabel sx={{ '& .MuiStepLabel-label': { fontSize: '0.65rem', mt: 0.5 } }}>
          {returnStages.map((stage) => {
            const cfg = DELIVERY_STATUS_CONFIG[stage];
            return (
              <Step key={stage}>
                <StepLabel>{isAr ? cfg.labelAr : cfg.labelEn}</StepLabel>
              </Step>
            );
          })}
        </Stepper>
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 1 }}>
      <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ mb: 1, display: 'block', fontSize: '0.75rem' }}>
        {isAr ? '🚚 تتبع التوصيل' : '🚚 Delivery Tracking'}
      </Typography>
      <Stepper activeStep={currentIdx} alternativeLabel sx={{ '& .MuiStepLabel-label': { fontSize: '0.65rem', mt: 0.5 } }}>
        {DELIVERY_STAGES.map((stage) => {
          const cfg = DELIVERY_STATUS_CONFIG[stage];
          return (
            <Step key={stage}>
              <StepLabel>{isAr ? cfg.labelAr : cfg.labelEn}</StepLabel>
            </Step>
          );
        })}
      </Stepper>
    </Box>
  );
};

// ═══════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════

export default function MyRentalsPage() {
  const { i18n } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const { user } = useCart();
  const { selectedCurrency, getCurrencySymbol } = useCurrency();
  const isAr = i18n.language === 'ar';
  const currencySymbol = getCurrencySymbol(selectedCurrency);

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancellingOrder, setCancellingOrder] = useState(null);
  const [cancelling, setCancelling] = useState(false);

  const customerId = user?.Id || user?.id;

  const loadOrders = useCallback(async () => {
    if (!customerId) return;
    setLoading(true);
    try {
      const data = await fetchCustomerRentals(customerId, selectedCurrency);
      let ordersList = [];
      if (Array.isArray(data)) ordersList = data;
      else if (data?.data && Array.isArray(data.data)) ordersList = data.data;
      else if (data?.Data && Array.isArray(data.Data)) ordersList = data.Data;
      else if (data?.rentals && Array.isArray(data.rentals)) ordersList = data.rentals;
      else if (data?.Rentals && Array.isArray(data.Rentals)) ordersList = data.Rentals;
      setOrders(ordersList);
    } catch (err) {
      console.error('Error loading rentals:', err);
      setSnackbar({ open: true, message: isAr ? 'فشل تحميل الطلبات' : 'Failed to load orders', severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [customerId, selectedCurrency, isAr]);

  useEffect(() => { loadOrders(); }, [loadOrders]);

  useEffect(() => {
    if (!user) navigate('/login');
  }, [user, navigate]);

  // Auto refresh every 30s for active orders
  useEffect(() => {
    const hasActive = orders.some(o => ['Pending', 'Confirmed', 'Active'].includes(o.Status));
    if (!hasActive) return;
    const interval = setInterval(loadOrders, 30000);
    return () => clearInterval(interval);
  }, [orders, loadOrders]);

  const TAB_FILTERS = [
    { label: isAr ? 'الكل' : 'All', filter: null },
    { label: isAr ? 'نشط' : 'Active', filter: ['Active', 'Confirmed'] },
    { label: isAr ? 'قيد الانتظار' : 'Pending', filter: ['Pending'] },
    { label: isAr ? 'مكتمل' : 'Completed', filter: ['Returned', 'Cancelled'] },
  ];

  const filteredOrders = orders.filter(o => {
    const f = TAB_FILTERS[tabValue]?.filter;
    return f ? f.includes(o.Status) : true;
  });

  const handleCancel = async () => {
    if (!cancellingOrder) return;
    setCancelling(true);
    try {
      await cancelRental(cancellingOrder.Id, customerId);
      setSnackbar({ open: true, message: isAr ? 'تم إلغاء الطلب' : 'Order cancelled', severity: 'success' });
      setCancelDialogOpen(false);
      setCancellingOrder(null);
      loadOrders();
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.ErrorMessage || (isAr ? 'فشل الإلغاء' : 'Cancel failed'), severity: 'error' });
    } finally {
      setCancelling(false);
    }
  };

  const so = selectedOrder;
  const soStatus = so ? STATUS_CONFIG[so.Status] : null;
  const soPriceType = so ? PRICE_TYPE_LABELS[so.RentalPriceType] : null;

  return (
    <Box sx={{ minHeight: '80vh', bgcolor: 'background.default', pb: { xs: 12, sm: 6 } }}>
      {/* Header */}
      <Box sx={{
        background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
        py: { xs: 4, sm: 5 }, px: 2,
        borderRadius: { xs: 0, sm: '0 0 32px 32px' },
      }}>
        <Container maxWidth="lg">
          <Stack direction="row" alignItems="center" spacing={2}>
            <IconButton onClick={() => navigate(-1)} sx={{ color: '#fff' }}><ArrowBackIcon /></IconButton>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h4" sx={{ color: '#fff', fontWeight: 900, fontSize: { xs: '1.5rem', sm: '1.8rem' } }}>
                {isAr ? 'طلبات التأجير' : 'My Rentals'}
              </Typography>
              <Typography variant="body2" sx={{ color: alpha('#fff', 0.8), fontSize: { xs: '0.8rem', sm: '0.9rem' } }}>
                {isAr ? `${orders.length} طلب` : `${orders.length} orders`}
              </Typography>
            </Box>
            <Tooltip title={isAr ? 'تحديث' : 'Refresh'}>
              <IconButton onClick={loadOrders} sx={{ color: '#fff' }}><RefreshIcon /></IconButton>
            </Tooltip>
          </Stack>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ mt: -2, px: { xs: 1.5, sm: 2, md: 3 } }}>
        {/* Tabs */}
        <Paper elevation={0} sx={{ borderRadius: 3, mb: 3, overflow: 'hidden', border: '1px solid', borderColor: alpha(theme.palette.divider, 0.08) }}>
          <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)} variant="scrollable" scrollButtons="auto"
            sx={{ '& .MuiTab-root': { textTransform: 'none', fontWeight: 700, fontSize: { xs: '0.8rem', sm: '0.9rem' }, minHeight: 48 }, '& .Mui-selected': { color: 'primary.main' } }}>
            {TAB_FILTERS.map((tab, i) => {
              const count = orders.filter(o => tab.filter ? tab.filter.includes(o.Status) : true).length;
              return <Tab key={i} label={<Badge badgeContent={count} color="primary" sx={{ '& .MuiBadge-badge': { fontSize: '0.65rem' } }}>{tab.label}</Badge>} />;
            })}
          </Tabs>
        </Paper>

        {/* Content */}
        {loading ? (
          <Stack spacing={2}>
            {[...Array(3)].map((_, i) => <Skeleton key={i} variant="rounded" height={200} sx={{ borderRadius: 4 }} />)}
          </Stack>
        ) : filteredOrders.length === 0 ? (
          <Paper elevation={0} sx={{ textAlign: 'center', py: { xs: 8, sm: 10 }, borderRadius: 4, border: '1px solid', borderColor: alpha(theme.palette.divider, 0.08) }}>
            <ReceiptLongIcon sx={{ fontSize: { xs: 60, sm: 80 }, color: alpha(theme.palette.text.secondary, 0.15), mb: 2 }} />
            <Typography variant="h6" fontWeight={700} color="text.secondary" sx={{ fontSize: { xs: '1rem', sm: '1.15rem' }, mb: 1 }}>
              {isAr ? 'لا توجد طلبات' : 'No orders found'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3, fontSize: { xs: '0.8rem', sm: '0.9rem' } }}>
              {isAr ? 'لم تقم بأي طلبات تأجير بعد' : "You haven't made any rental orders yet"}
            </Typography>
            <Button variant="contained" onClick={() => navigate('/rentals')} startIcon={<HomeRepairServiceIcon />}
              sx={{ borderRadius: 3, fontWeight: 700, px: 4 }}>
              {isAr ? 'تصفح المنتجات' : 'Browse Products'}
            </Button>
          </Paper>
        ) : (
          <Stack spacing={0}>
            {filteredOrders.map((order, i) => (
              <RentalOrderCard key={order.Id} order={order} index={i} isAr={isAr} currencySymbol={currencySymbol}
                onViewDetails={(o) => { setSelectedOrder(o); setDetailDialogOpen(true); }}
                onCancel={(o) => { setCancellingOrder(o); setCancelDialogOpen(true); }}
              />
            ))}
          </Stack>
        )}
            </Container>

      {/* ═══════════════════════════════════════════
           DETAIL DIALOG
         ═══════════════════════════════════════════ */}
      <Dialog open={detailDialogOpen} onClose={() => setDetailDialogOpen(false)} maxWidth="sm" fullWidth fullScreen={isMobile}
        PaperProps={{ sx: { borderRadius: isMobile ? 0 : 4, maxHeight: isMobile ? '100%' : '90vh' } }}>

        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Typography fontWeight={800} sx={{ fontSize: { xs: '1rem', sm: '1.15rem' } }}>
            {isAr ? 'تفاصيل الطلب' : 'Order Details'}{' '}
            <Chip label={`#${so?.Id}`} size="small" color="primary" sx={{ fontWeight: 700, ml: 0.5 }} />
          </Typography>
          <IconButton onClick={() => setDetailDialogOpen(false)}><CloseIcon /></IconButton>
        </DialogTitle>

        {so && (
          <DialogContent dividers sx={{ p: { xs: 2, sm: 2.5 } }}>
            <Stack spacing={2.5}>

              {/* Product + Status */}
              <Stack direction="row" spacing={2} alignItems="center">
                <Avatar src={getImageUrl(so.ProductImage)} variant="rounded" sx={{ width: 64, height: 64, borderRadius: 2 }}>
                  <HomeRepairServiceIcon />
                </Avatar>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography fontWeight={800} noWrap sx={{ fontSize: '1rem' }}>{so.ProductName}</Typography>
                  <Stack direction="row" spacing={0.5} sx={{ mt: 0.5, flexWrap: 'wrap', gap: 0.3 }}>
                    <Chip
                      icon={React.cloneElement(soStatus?.icon || <ScheduleIcon />, { sx: { fontSize: 14 } })}
                      label={isAr ? soStatus?.labelAr : soStatus?.labelEn}
                      size="small"
                      sx={{
                        bgcolor: alpha(soStatus?.color || '#999', 0.1),
                        color: soStatus?.color,
                        fontWeight: 700, fontSize: '0.7rem',
                        '& .MuiChip-icon': { color: 'inherit' },
                      }}
                    />
                    {so.SelectedColor && (
                      <Chip icon={<PaletteIcon sx={{ fontSize: 12 }} />} label={so.SelectedColor} size="small" variant="outlined" sx={{ fontSize: '0.65rem' }} />
                    )}
                    {so.IsDelivery ? (
                      <Chip icon={<LocalShippingIcon sx={{ fontSize: 12 }} />} label={isAr ? 'توصيل' : 'Delivery'} size="small" color="info" variant="outlined" sx={{ fontSize: '0.65rem' }} />
                    ) : (
                      <Chip icon={<StorefrontIcon sx={{ fontSize: 12 }} />} label={isAr ? 'من المتجر' : 'Pickup'} size="small" variant="outlined" sx={{ fontSize: '0.65rem' }} />
                    )}
                  </Stack>
                </Box>
              </Stack>

              {/* Delivery Tracking */}
              {so.IsDelivery && so.DeliveryStatus && (
                <Box sx={{ p: 2, borderRadius: 2, bgcolor: alpha(theme.palette.info.main, 0.03), border: '1px solid', borderColor: alpha(theme.palette.info.main, 0.1) }}>
                  <DeliveryTracker order={so} isAr={isAr} />
                </Box>
              )}

              {/* Rental Period */}
              <Box sx={{ p: 2, borderRadius: 2, bgcolor: alpha(theme.palette.warning.main, 0.03), border: '1px solid', borderColor: alpha(theme.palette.warning.main, 0.1) }}>
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5, fontSize: '0.9rem' }}>
                  {isAr ? '⏱️ فترة التأجير' : '⏱️ Rental Period'}
                </Typography>
                <DetailRow
                  label={isAr ? 'تاريخ البداية' : 'Start Date'}
                  value={new Date(so.RentalStartDate).toLocaleDateString(isAr ? 'ar-EG' : 'en-US', { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' })}
                />
                <DetailRow
                  label={isAr ? 'تاريخ النهاية' : 'End Date'}
                  value={new Date(so.RentalEndDate).toLocaleDateString(isAr ? 'ar-EG' : 'en-US', { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' })}
                />
                <DetailRow
                  label={isAr ? 'المدة' : 'Duration'}
                  value={`${so.RentalDuration} ${isAr ? soPriceType?.ar : soPriceType?.en}`}
                />
                {so.ActualReturnDate && (
                  <DetailRow
                    label={isAr ? 'تاريخ الإرجاع الفعلي' : 'Actual Return'}
                    value={new Date(so.ActualReturnDate).toLocaleDateString(isAr ? 'ar-EG' : 'en-US', { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' })}
                    color={new Date(so.ActualReturnDate) > new Date(so.RentalEndDate) ? 'error.main' : 'success.main'}
                  />
                )}
              </Box>

              {/* Delivery Info */}
              <Box sx={{
                p: 2, borderRadius: 2,
                bgcolor: so.IsDelivery ? alpha(theme.palette.info.main, 0.03) : alpha(theme.palette.grey[500], 0.03),
                border: '1px solid',
                borderColor: so.IsDelivery ? alpha(theme.palette.info.main, 0.1) : alpha(theme.palette.divider, 0.08),
              }}>
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5, fontSize: '0.9rem' }}>
                  {so.IsDelivery ? (isAr ? '🚚 معلومات التوصيل' : '🚚 Delivery Info') : (isAr ? '🏪 استلام من المتجر' : '🏪 In-Store Pickup')}
                </Typography>

                {so.IsDelivery ? (
                  <>
                    {so.DeliveryAddress && (
                      <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ mb: 1 }}>
                        <LocationOnIcon sx={{ fontSize: 16, color: 'info.main', mt: 0.3 }} />
                        <Typography variant="body2" sx={{ fontSize: '0.82rem' }}>{so.DeliveryAddress}</Typography>
                      </Stack>
                    )}
                    {so.DriverName && (
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                        <DirectionsBikeIcon sx={{ fontSize: 16, color: 'info.main' }} />
                        <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.82rem' }}>
                          {so.DriverName}
                        </Typography>
                        {so.DriverPhone && (
                          <Chip
                            icon={<PhoneIcon sx={{ fontSize: 12 }} />}
                            label={so.DriverPhone}
                            size="small"
                            variant="outlined"
                            color="info"
                            sx={{ fontSize: '0.65rem', height: 22 }}
                            onClick={() => window.open(`tel:${so.DriverPhone}`)}
                          />
                        )}
                      </Stack>
                    )}
                    {so.DeliveryStatus && (
                      <DetailRow
                        label={isAr ? 'حالة التوصيل' : 'Delivery Status'}
                        value={isAr ? DELIVERY_STATUS_CONFIG[so.DeliveryStatus]?.labelAr : DELIVERY_STATUS_CONFIG[so.DeliveryStatus]?.labelEn}
                        color={DELIVERY_STATUS_CONFIG[so.DeliveryStatus]?.color}
                      />
                    )}
                  </>
                ) : (
                  <Stack direction="row" spacing={1} alignItems="center">
                    <StorefrontIcon sx={{ fontSize: 18, color: 'success.main' }} />
                    <Typography variant="body2" sx={{ fontSize: '0.82rem' }}>
                      {isAr ? 'يرجى الحضور للمتجر لاستلام المنتج' : 'Please visit the store to pick up your product'}
                    </Typography>
                  </Stack>
                )}
              </Box>

              {/* Payment Details */}
              <Box sx={{ p: 2, borderRadius: 2, bgcolor: alpha(theme.palette.success.main, 0.03), border: '1px solid', borderColor: alpha(theme.palette.success.main, 0.1) }}>
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5, fontSize: '0.9rem' }}>
                  {isAr ? '💰 تفاصيل المبلغ' : '💰 Payment Details'}
                </Typography>
                <DetailRow
                  label={isAr ? 'سعر الوحدة' : 'Price/Unit'}
                  value={`${(so.RentalPricePerUnit || 0).toLocaleString()} ${currencySymbol}/${isAr ? soPriceType?.arSingle : soPriceType?.enSingle}`}
                />
                <DetailRow
                  label={isAr ? 'إجمالي الإيجار' : 'Rental Total'}
                  value={`${(so.RentalTotalPrice || 0).toLocaleString()} ${currencySymbol}`}
                />
                {(so.DepositAmount || 0) > 0 && (
                  <DetailRow
                    label={isAr ? 'التأمين' : 'Deposit'}
                    value={`${(so.DepositAmount || 0).toLocaleString()} ${currencySymbol}${so.DepositReturned ? (isAr ? ' ✅ مسترد' : ' ✅ Returned') : ''}`}
                    color={so.DepositReturned ? 'success.main' : undefined}
                  />
                )}
                {so.IsDelivery && (
                  <DetailRow
                    label={isAr ? 'رسوم التوصيل' : 'Delivery Fee'}
                    value={(so.DeliveryFee || 0) > 0 ? `${(so.DeliveryFee).toLocaleString()} ${currencySymbol}` : (isAr ? 'مجاني' : 'Free')}
                    color="info.main"
                  />
                )}
                {(so.LateFee || 0) > 0 && (
                  <DetailRow
                    label={isAr ? 'رسوم التأخير' : 'Late Fee'}
                    value={`${(so.LateFee).toLocaleString()} ${currencySymbol}`}
                    color="error.main"
                  />
                )}
                <Divider sx={{ my: 1 }} />
                <DetailRow
                  label={isAr ? 'المبلغ الكلي' : 'Grand Total'}
                  value={`${(so.GrandTotal || so.RentalTotalPrice || 0).toLocaleString()} ${currencySymbol}`}
                  color="primary.main"
                  bold
                />
              </Box>

              {/* Overdue Alert */}
              {so.IsOverdue && (
                <Alert severity="error" sx={{ borderRadius: 2 }}>
                  {isAr ? '⚠️ هذا الطلب متأخر عن موعد الإرجاع! يرجى إعادة المنتج في أقرب وقت لتجنب رسوم إضافية.' : '⚠️ This order is overdue! Please return the product as soon as possible to avoid additional fees.'}
                </Alert>
              )}

              {/* Notes */}
              {so.CustomerNotes && (
                <Box sx={{ p: 1.5, bgcolor: alpha(theme.palette.info.main, 0.04), borderRadius: 2 }}>
                  <Typography variant="caption" fontWeight={700} color="text.secondary">{isAr ? '📝 ملاحظاتك:' : '📝 Your Notes:'}</Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.82rem', mt: 0.3 }}>{so.CustomerNotes}</Typography>
                </Box>
              )}
              {so.AdminNotes && (
                <Box sx={{ p: 1.5, bgcolor: alpha(theme.palette.warning.main, 0.04), borderRadius: 2 }}>
                  <Typography variant="caption" fontWeight={700} color="text.secondary">{isAr ? '🏪 ملاحظات المتجر:' : '🏪 Store Notes:'}</Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.82rem', mt: 0.3 }}>{so.AdminNotes}</Typography>
                </Box>
              )}

              {/* Order metadata */}
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', textAlign: 'center' }}>
                {isAr ? 'تاريخ الطلب:' : 'Order Date:'} {new Date(so.CreatedDate).toLocaleString(isAr ? 'ar-EG' : 'en-US')}
              </Typography>

            </Stack>
          </DialogContent>
        )}

        <DialogActions sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider', gap: 1 }}>
          {so && (so.Status === 'Pending' || so.Status === 'Confirmed') && (
            <Button variant="outlined" color="error" onClick={() => { setDetailDialogOpen(false); setCancellingOrder(so); setCancelDialogOpen(true); }}
              startIcon={<CancelIcon />} sx={{ borderRadius: 3, fontWeight: 700, flex: isMobile ? 1 : 'none' }}>
              {isAr ? 'إلغاء الطلب' : 'Cancel Order'}
            </Button>
          )}
          <Button variant="contained" onClick={() => setDetailDialogOpen(false)} fullWidth={!(so && (so.Status === 'Pending' || so.Status === 'Confirmed'))}
            sx={{ borderRadius: 3, fontWeight: 700, flex: isMobile ? 1 : 'none' }}>
            {isAr ? 'إغلاق' : 'Close'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ═══════════════════════════════════════════
           CANCEL DIALOG
         ═══════════════════════════════════════════ */}
      <Dialog open={cancelDialogOpen} onClose={() => setCancelDialogOpen(false)}
        PaperProps={{ sx: { borderRadius: 4, m: 2 } }}>
        <DialogTitle sx={{ fontWeight: 800, pb: 1 }}>{isAr ? 'تأكيد الإلغاء' : 'Confirm Cancellation'}</DialogTitle>
        <DialogContent>
          <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
            <Avatar src={getImageUrl(cancellingOrder?.ProductImage)} variant="rounded" sx={{ width: 48, height: 48 }}>
              <HomeRepairServiceIcon />
            </Avatar>
            <Box>
              <Typography fontWeight={700} sx={{ fontSize: '0.95rem' }}>{cancellingOrder?.ProductName}</Typography>
              <Typography variant="caption" color="text.secondary">
                #{cancellingOrder?.Id} • {cancellingOrder?.RentalDuration}{' '}
                {isAr ? PRICE_TYPE_LABELS[cancellingOrder?.RentalPriceType]?.ar : PRICE_TYPE_LABELS[cancellingOrder?.RentalPriceType]?.en}
              </Typography>
            </Box>
          </Stack>
          <Alert severity="warning" sx={{ borderRadius: 2, fontSize: '0.82rem' }}>
            {isAr ? 'هل أنت متأكد؟ لا يمكن التراجع عن هذا الإجراء.' : 'Are you sure? This action cannot be undone.'}
          </Alert>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={() => setCancelDialogOpen(false)} sx={{ borderRadius: 3, fontWeight: 600 }}>
            {isAr ? 'لا، تراجع' : 'No, Go Back'}
          </Button>
          <Button variant="contained" color="error" onClick={handleCancel} disabled={cancelling}
            startIcon={cancelling ? <CircularProgress size={16} /> : <CancelIcon />}
            sx={{ borderRadius: 3, fontWeight: 700 }}>
            {cancelling ? (isAr ? 'جاري الإلغاء...' : 'Cancelling...') : (isAr ? 'نعم، إلغاء' : 'Yes, Cancel')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ═══════════════════════════════════════════
           SNACKBAR
         ═══════════════════════════════════════════ */}
      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar(p => ({ ...p, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert onClose={() => setSnackbar(p => ({ ...p, open: false }))} severity={snackbar.severity} variant="filled"
          sx={{ width: '100%', borderRadius: 3, fontWeight: 600 }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}